import { useMemo } from 'react';

import { DayTag } from '@/components/DayTag';
import { HTMLParagraph } from '@/components/HTMLParagraph';
import { EnforcePageWidth, CenteredText, DualSection } from '@/layout/PageLayout';

import { getMainContentItineraryBlock } from './helpers/helpers';
import { StyledBannerImage, StyledImage } from './styles';

import { Carousel } from '../Carousel';
import { GradientSpacer } from '../GradientSpacer';
import { ItineraryTips } from '../ItineraryTips';
import { Paragraph } from '../Paragraph';
import { Spacer } from '../Spacer';

import type { Content } from '@/types/content';
import type { ItineraryBlock } from '@/types/itinerary';

interface ProgramBlockProps {
  itineraryBlock: ItineraryBlock;
}

function ProgramBlock({ itineraryBlock }: ProgramBlockProps) {
  // useEffect(() => {
  //   console.log('ProgramBlock rendered');
  //   console.log(itineraryBlock);
  // }, [itineraryBlock]);

  const mainContent = useMemo(() => getMainContentItineraryBlock(itineraryBlock), [itineraryBlock]);
  const headerImage = useMemo(
    () => mainContent?.RichContent?.filter((content) => content.subType?.toLowerCase() === 'header')[0]?.path,
    [mainContent],
  );

  const remainingImages = useMemo(
    () => mainContent?.RichContent?.filter((content) => content.subType?.toLowerCase() !== 'header').slice(0, 4),
    [mainContent],
  );

  const cards = remainingImages?.map((image, index) => ({
    id: String(index),
    element: <StyledImage src={image.path} alt={image.type} />,
  }));

  const tips = useMemo(
    () => itineraryBlock.Content?.filter((content: Content) => content.type.toLowerCase() === 'tip'),
    [itineraryBlock],
  );

  if (!mainContent) return null;

  return (
    <div id={`program-section-${itineraryBlock.id}`}>
      <GradientSpacer />
      <DayTag startDay={itineraryBlock.startDay} endDay={itineraryBlock.endDay} />
      <CenteredText>
        <h2>{mainContent.header}</h2>
      </CenteredText>
      <CenteredText>
        <h4>{mainContent.subHeader}</h4>
      </CenteredText>
      <StyledBannerImage src={headerImage} />
      <EnforcePageWidth>
        <Spacer size="xl" />
        <DualSection>
          <HTMLParagraph weight={700} dangerousHTML={mainContent.paragraph ?? ''} />
          <Paragraph>{mainContent.subParagraph}</Paragraph>
        </DualSection>
        <Spacer size="sm" />
        {cards && (
          <Carousel
            alignControls="right"
            startAtIndex={1}
            showOverview={false}
            maxCardHeightPx={400}
            cards={cards}
            fullWidthControls
          />
        )}
        <Spacer size="xl" />

        {tips && <ItineraryTips tips={tips} location={itineraryBlock.location} />}
        <Spacer size="2xl" />
      </EnforcePageWidth>
    </div>
  );
}

export { ProgramBlock };
